import { get, set } from 'lodash';

import { captureException, getUrlAndServiceName } from '@shared/utils';
import { API_STATUS_CODES, SENTRY_ERROR_TYPES } from '@shared/constants';
import { CsrfService } from '@shared/services';
import { IApiError, IRequestConfig } from '@shared/types';

import { API_ERROR, INVALID_CSRF_ERROR_CODE } from '@/constants';
import Constants from '@/utils/constants';
import handleError, {
	generateBrowserFingerprint,
	getFingerprintCookie,
	refreshFingerprintToken,
} from '@/utils/helper';
import history from '@/utils/history';
import { store } from '@/redux/store/store';
import { sendApiErrorEvent } from '@/utils/AnalyticsUtils';
import { RoutePath } from '@/types';
import TestModeService from '@/utils/testMode.service';

import { pfbAnalyticsClient } from './analyticsClient';
import { removeData, setDestinationURL } from './cache';

const csrfService = new CsrfService({
	baseURL: Constants.API_WEB_BASE_URL,
	timeout: Constants.API_TIMEOUT,
	headers: Constants.HEADERS,
	csrfCookieKey: Constants.X_CSRF_COOKIE,
	csrfKey: Constants.X_CSRF_TOKEN,
});

/**
 * @description Used to return the browser fingerprint in a backward compatible system identified format.
 *
 * @param {string} fingerprint
 * @returns {string}
 */
const _generateBrowserFingerprint = (fingerprint: string) => {
	/**
	 * In order to support the backward compatibility on Gendalf JS client for supporting the new
	 * Fingerprint library we need to send the fingerprint in the below mentioned format.
	 * Earlier using the Crysp library we were picking up the osid + xdhp + xwgl + xdpi values from Crysp
	 * library generated response and concatenated with dot and send this to backend and the same order was
	 * validated on the backend.
	 * For more info: https://gitlab.phonepe.com/ppe-business/insights-web/-/merge_requests/852/diffs#c836157439f1d6975a60210c46eab83e9b2ffae2_67_57
	 */
	return `${fingerprint}.${fingerprint}.${fingerprint}.${fingerprint}`;
};

const _getBrowserFingerprint = () => {
	const visitorId = getFingerprintCookie();

	return visitorId ? _generateBrowserFingerprint(visitorId) : '';
};

const _getDeviceFingerprint = () => getFingerprintCookie();

const removeCachedData = () => {
	pfbAnalyticsClient.flush();
	removeData(Constants.LOGIN_CHECK_KEY);
	removeData(Constants.USER_PERMISSIONS_KEY);
	removeData(Constants.USER_INFO_KEY);
	removeData(Constants.MERCHANT_PROFILE_KEY);
	removeData(Constants.MERCHANT_LIST_KEY);
	removeData(Constants.USER_GROUPS_LIST_KEY);
	removeData(Constants.SELECTED_MID);
	removeData(Constants.TEST_MODE_AUTH_KEY);
	refreshFingerprintToken();
	setDestinationURL();
	setTimeout(() => history.push(RoutePath.LOGIN), 0);
};

const checkCSRFinResErrorHandler = (
	error: IApiError<{ errorCode?: string; message?: string }>,
) => {
	csrfService.setCSRFToken(error?.response?.headers);

	for (const url of Constants.UNAUTHORISED_EXCEPTION_URLS) {
		if (error?.request?.responseURL.includes(url)) {
			return Promise.reject(error?.response);
		}
	}

	if (
		error?.response?.status === Constants.UNAUTHORISED_CODE &&
		error.response?.data?.errorCode !== INVALID_CSRF_ERROR_CODE
	) {
		// For "invalid CSRF", not logging out the user, just removing the CSRF cookie. This will reduce number of logouts.
		removeCachedData();
	} else {
		return error;
	}
};

const captureAutoLoggedOutUser = (error: IApiError) => {
	if (error?.response?.status !== Constants.UNAUTHORISED_CODE) {
		return;
	}
	const storeCopy = store.getState();
	const userId = get(storeCopy, 'userAuth.user.userId', '');

	const requestHeaders = get(error, 'config.headers', {});
	const responseHeaders = get(error, 'response.headers', {});

	const payload = {
		request: {
			headers: requestHeaders,
			response: get(error, 'request.response', {}),
		},
		response: {
			headers: responseHeaders,
			response:
				get(error, 'response.data.message', '') ||
				get(error, 'response.data.errors[0]', ''),
		},
		userId: userId,
	};

	captureException(error, {
		tags: {
			APIError_autoLogout: get(error, 'config.url', ''),
			errorType: SENTRY_ERROR_TYPES.AutoLogout,
		},
		extras: {
			payload: {
				variables: get(error, 'config.params', {}),
				payload,
				statusCode: get(error, 'response.status', 'NA'),
			},
		},
	});
};

const APIErrorHandler = (error: IApiError) => {
	const data = error?.response?.data ?? { code: error.code };
	const status = error?.response?.status ?? API_STATUS_CODES.IM_A_TEAPOT;
	return handleError({ data, status });
};

const responseErrorHandler = async (error: IApiError<{ message: string }>) => {
	captureAutoLoggedOutUser(error);
	await checkCSRFinResErrorHandler(error);

	const authType = JSON.parse(
		localStorage.getItem(Constants.LOGIN_CHECK_KEY) ?? '{}',
	).authType;
	const { url = '', baseURL = '' } = error?.config ?? {};
	const { fullUrl, serviceName } = getUrlAndServiceName(baseURL, url);
	const errorObject = {
		url: fullUrl,
		authType,
		serviceName,
		errorResponse: error.request.responseText,
		statusCode: error.response?.status ?? API_STATUS_CODES.IM_A_TEAPOT,
	};
	sendApiErrorEvent({
		name: API_ERROR,
		value: error.response?.data?.message,
		additionalData: errorObject,
	});

	set(error, 'response.data', await APIErrorHandler(error));

	if (
		error.response?.status !== API_STATUS_CODES.PRECONDITION_FAILED &&
		error.response?.status !== API_STATUS_CODES.UNAUTHORIZED
	) {
		captureException(error, {
			tags: {
				errorType: SENTRY_ERROR_TYPES.API,
				apiStatusCode: String(
					error.response?.status ?? API_STATUS_CODES.IM_A_TEAPOT,
				),
			},
		});
	}
	return Promise.reject(error.response);
};
export const apiClientResponseErrorHandler = (
	error: IApiError<{ message: string }>,
) => {
	if (error.config?.method === 'post') {
		csrfService.removeCSRFToken();
	}
	return responseErrorHandler(error);
};

const attachBrowserFingerprint = (request: IRequestConfig) => {
	const browserFingerprint = _getBrowserFingerprint();
	if (request && browserFingerprint) {
		request.headers![Constants.FINGERPRINT] = browserFingerprint;
		return Promise.resolve(request);
	}
	return generateBrowserFingerprint()
		.then((res) => {
			request.headers![Constants.FINGERPRINT] =
				_generateBrowserFingerprint(res);
			return request;
		})
		.catch(() => {
			return request;
		});
};

const attachDeviceFingerprint = (request: IRequestConfig) => {
	const deviceFingerprint = _getDeviceFingerprint();
	request.headers = request.headers ?? {};
	if (request && deviceFingerprint) {
		request.headers[Constants.X_DEVICE_FINGERPRINT] = deviceFingerprint;
		return Promise.resolve(request);
	}

	return generateBrowserFingerprint()
		.then((res) => {
			request.headers![Constants.X_DEVICE_FINGERPRINT] = res;
			return request;
		})
		.catch(() => {
			return request;
		});
};

const attachTestMode = (request: IRequestConfig) => {
	if (!request.headers) return request;

	const testModeAuthToken = TestModeService.getTestModeToken();
	if (testModeAuthToken) {
		request.headers.Authorization = `Bearer ${testModeAuthToken}`;
	}
	const browserFingerprint = _getBrowserFingerprint();
	if (browserFingerprint) {
		request.headers[Constants.X_DEVICE_FINGERPRINT] = browserFingerprint;
	}
	return request;
};

export {
	checkCSRFinResErrorHandler,
	attachBrowserFingerprint,
	attachDeviceFingerprint,
	responseErrorHandler,
	attachTestMode,
};
